import React, { useState, useEffect, createContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { Box, CircularProgress } from '@mui/material';

import Layout from './components/Layout';
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import Dashboard from './pages/Dashboard';
import ProjectsList from './pages/ProjectsList';
import ProjectDetail from './pages/ProjectDetail';
import NewQuote from './pages/NewQuote';
import PurchaseOrders from './pages/PurchaseOrders';
import PurchaseOrderDetail from './pages/PurchaseOrderDetail';
import ProfileSettings from './pages/ProfileSettings';
import api from './services/api';
import AIAssistant from './pages/AIAssistant';
import Workspace from './pages/Workspace';
import SalesLanding from './pages/SalesLanding';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Contact from './pages/Contact';
import Guide from './pages/Guide';
import MyTime from './pages/MyTime';
import TimeSheets from './pages/TimeSheets';
import Tools from './pages/Tools';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import { getStoredAdminUser, getAdminToken, clearAdminAuth } from './services/adminApi';
import adminApi from './services/adminApi';
import QuotingTemplate from './pages/tools/QuotingTemplate';
import PurchaseOrderTemplate from './pages/tools/PurchaseOrderTemplate';
import InvoiceTemplate from './pages/tools/InvoiceTemplate';
import PublicPlanShare from './pages/PublicPlanShare';
import disableTextPrediction from './utils/disableTextPrediction';
import DownloadIndicator from './components/DownloadIndicator';


// Create theme based on mode
const getTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: '#0087c1',
      light: '#4da8d4',
      dark: '#006494',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: mode === 'light' ? '#f5f7fa' : '#101418',
      paper: mode === 'light' ? '#ffffff' : '#1a1e24',
    },
    divider: mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#006494',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 12,
        },
      },
      defaultProps: {
        elevation: 0,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          backgroundImage: 'none',
          boxShadow: mode === 'light'
            ? '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)'
            : '0 1px 3px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.3)',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          minHeight: 44,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontWeight: 600,
            backgroundColor: mode === 'light' ? '#f5f7fa' : '#1a1e24',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: mode === 'light' ? 'rgba(0,135,193,0.08)' : 'rgba(0,135,193,0.16)',
            '&:hover': {
              backgroundColor: mode === 'light' ? 'rgba(0,135,193,0.12)' : 'rgba(0,135,193,0.24)',
            },
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: mode === 'light' ? '#f5f7fa' : '#1a1e24',
            borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: mode === 'light' ? 'rgba(0,135,193,0.04)' : 'rgba(0,135,193,0.08)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

// Protected Route component
const ProtectedRoute = ({ children, isAuthenticated }) => {
  return isAuthenticated ? children : <Navigate to="/" replace />;
};

// Role-based route protection component
const RoleProtectedRoute = ({ children, currentUser, allowedRoles }) => {
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(currentUser.role)) {
    // Redirect Site users to projects if they try to access restricted routes
    return <Navigate to="/projects" replace />;
  }
  
  return children;
};

// Get the default route based on user role
const getDefaultRoute = (userRole) => {
  // Site and Site Manager users default to projects list
  return (userRole === 'Site' || userRole === 'Site Manager') ? '/projects' : '/dashboard';
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export const AppContext = createContext();

function App() {
  useEffect(disableTextPrediction, []);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Admin auth state (completely separate from tenant auth)
  const [adminUser, setAdminUser] = useState(null);
  const [adminLoading, setAdminLoading] = useState(true);

  useEffect(() => {
    const checkAdminAuth = async () => {
      const token = getAdminToken();
      const stored = getStoredAdminUser();
      if (token && stored) {
        try {
          const res = await adminApi.me();
          setAdminUser(res.data);
        } catch {
          clearAdminAuth();
          setAdminUser(null);
        }
      }
      setAdminLoading(false);
    };
    checkAdminAuth();
  }, []);

  const handleAdminLogin = (user) => setAdminUser(user);
  const handleAdminLogout = () => setAdminUser(null);

  const theme = getTheme(darkMode ? 'dark' : 'light');

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newMode));
      return newMode;
    });
  };

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuthStatus = async () => {
      const token = localStorage.getItem('authToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const storedUser = localStorage.getItem('currentUser');

      if ((token || refreshToken) && storedUser) {
        try {
          if (token) {
            api.setAuthToken(token);
          }
          const response = await api.authApi.getCurrentUser();

          setCurrentUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Token verification failed:', error);
          api.clearStoredAuth();
          setIsAuthenticated(false);
          setCurrentUser(null);
        }
      }
      setLoading(false);
    };

    checkAuthStatus();
  }, []);

  const handleLogin = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    api.clearStoredAuth();
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '100vh' 
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <AppContext.Provider value={{ currentUser }}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <CssBaseline />
          <ScrollToTop />
          <DownloadIndicator />
          <Routes>
            {/* Admin routes — fully independent auth */}
            <Route
              path="/admin"
              element={
                adminUser ? (
                  <Navigate to="/admin/dashboard" replace />
                ) : adminLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>
                ) : (
                  <AdminLogin onLogin={handleAdminLogin} />
                )
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                adminLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}><CircularProgress /></Box>
                ) : adminUser ? (
                  <AdminDashboard onLogout={handleAdminLogout} />
                ) : (
                  <Navigate to="/admin" replace />
                )
              }
            />

            {/* Public routes */}
            <Route 
              path="/" 
              element={
                isAuthenticated ? (
                  <Navigate to={getDefaultRoute(currentUser?.role)} replace />
                ) : (
                  <SalesLanding />
                )
              } 
            />
            <Route 
              path="/login" 
              element={
                isAuthenticated ? (
                  <Navigate to={getDefaultRoute(currentUser?.role)} replace />
                ) : (
                  <Login onLogin={handleLogin} />
                )
              } 
            />
            <Route 
              path="/register" 
              element={
                isAuthenticated ? (
                  <Navigate to={getDefaultRoute(currentUser?.role)} replace />
                ) : (
                  <Register onLogin={handleLogin} />
                )
              } 
            />
            <Route
              path="/verify-email"
              element={
                isAuthenticated ? (
                  <Navigate to={getDefaultRoute(currentUser?.role)} replace />
                ) : (
                  <VerifyEmail />
                )
              }
            />
            
            {/* Legal document routes */}
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Marketing pages */}
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/guide" element={<Guide />} />

            {/* Public tools */}
            <Route path="/tools/quote-template" element={<QuotingTemplate />} />
            <Route path="/tools/purchase-order-template" element={<PurchaseOrderTemplate />} />
            <Route path="/tools/invoice-template" element={<InvoiceTemplate />} />
            <Route path="/share/plan/:token" element={<PublicPlanShare />} />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  <Layout currentUser={currentUser} onLogout={handleLogout} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
                    <Routes>
                      {/* Default route redirects based on user role */}
                      <Route path="/" element={<Navigate to={getDefaultRoute(currentUser?.role)} replace />} />
                      
                      {/* Dashboard - Office users only */}
                      <Route 
                        path="/dashboard" 
                        element={
                          <RoleProtectedRoute currentUser={currentUser} allowedRoles={['Office']}>
                            <Dashboard />
                          </RoleProtectedRoute>
                        } 
                      />
                      
                      {/* Projects - All authenticated users */}
                      <Route path="/projects" element={<ProjectsList />} />
                      <Route path="/projects/:projectId" element={<ProjectDetail />} />
                      
                      {/* New Quote - Office users only */}
                      <Route 
                        path="/new-quote" 
                        element={
                          <RoleProtectedRoute currentUser={currentUser} allowedRoles={['Office']}>
                            <NewQuote />
                          </RoleProtectedRoute>
                        } 
                      />
                      
                      {/* Workspace - Office and Site Manager users */}
                      <Route
                        path="/workspace"
                        element={
                          <RoleProtectedRoute currentUser={currentUser} allowedRoles={['Office', 'Site Manager']}>
                            <Workspace currentUser={currentUser} />
                          </RoleProtectedRoute>
                        }
                      />
                      
                      {/* Purchase Orders - Office and Site Manager users */}
                      <Route 
                        path="/purchase-orders" 
                        element={
                          <RoleProtectedRoute currentUser={currentUser} allowedRoles={['Office', 'Site Manager']}>
                            <PurchaseOrders />
                          </RoleProtectedRoute>
                        } 
                      />
                      <Route 
                        path="/purchase-orders/:orderId" 
                        element={
                          <RoleProtectedRoute currentUser={currentUser} allowedRoles={['Office', 'Site Manager']}>
                            <PurchaseOrderDetail />
                          </RoleProtectedRoute>
                        } 
                      />
                      
                      
                      {/* Profile Settings - All authenticated users */}
                      <Route path="/profile-settings" element={<ProfileSettings />} />

                      {/* My Time - All authenticated users */}
                      <Route path="/my-time" element={<MyTime />} />

                      {/* Time Sheets - Office users only (admin view) */}
                      <Route 
                        path="/time-sheets" 
                        element={
                          <RoleProtectedRoute currentUser={currentUser} allowedRoles={['Office']}>
                            <TimeSheets />
                          </RoleProtectedRoute>
                        } 
                      />

                      {/* Tools - Office users only */}
                      <Route
                        path="/tools"
                        element={
                          <RoleProtectedRoute currentUser={currentUser} allowedRoles={['Office']}>
                            <Tools />
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Legacy routes redirected to Workspace tabs */}
                      <Route path="/forms" element={<Navigate to="/workspace?tab=forms" replace />} />
                      <Route path="/company-settings" element={<Navigate to="/workspace?tab=company-settings" replace />} />
                      <Route path="/saved-items" element={<Navigate to="/workspace?tab=saved-items" replace />} />
                      <Route path="/vendors" element={<Navigate to="/workspace?tab=vendors" replace />} />
                      <Route path="/organisations" element={<Navigate to="/workspace?tab=organisations" replace />} />
                      <Route path="/workers" element={<Navigate to="/workspace?tab=users" replace />} />

                      {/* AI Assistant - Office users only */}
                      <Route 
                        path="/ai-assistant" 
                        element={
                          <RoleProtectedRoute currentUser={currentUser} allowedRoles={['Office']}>
                            <AIAssistant />
                          </RoleProtectedRoute>
                        } 
                      />

                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </LocalizationProvider>
      </ThemeProvider>
    </AppContext.Provider>
  );
}

export default App;
